/* global axios */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import debounce from 'lodash.debounce';
import api from '../services/apiService';
import { formatDateDE } from '../utils/dateFormatter';

function CustrevFromInvoiceReport() {
  // Basis-Filter
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Customer-Filter
  const [customerCategoryId, setCustomerCategoryId] = useState('');
  const [customerGroupId, setCustomerGroupId] = useState('');
  const [postalCodeFrom, setPostalCodeFrom] = useState('');
  const [postalCodeTo, setPostalCodeTo] = useState('');

  // 🔹 Umsatz-Range (Netto, wirkt auf SUM(totalNet) je Kunde)
  const [sumNetFrom, setSumNetFrom] = useState('');
  const [sumNetTo, setSumNetTo] = useState('');

  

  // Sortierung
  const [orderBy, setOrderBy] = useState('sumGross'); // sumGross|sumNet|invoiceCount|lastInvoiceDate
  const [orderDir, setOrderDir] = useState('DESC');   // ASC|DESC

  // Paging + Daten
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [sumNet, setSumNet] = useState(0);
  const [sumGross, setSumGross] = useState(0);
  const [amountPaid, setAmountPaid] = useState(0);
  const [outstanding, setOutstanding] = useState(0);

  // UI
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Options
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);


  // expand + lazy invoices + overview
  const [expanded, setExpanded] = useState({});
  const [invoicesByCustomerId, setInvoicesByCustomerId] = useState({});
  const [overviewByCustomerId, setOverviewByCustomerId] = useState({}); // ⬅️ NEU: Monats-/Artikel-Überblick
  const [rowInvoiceLoading, setRowInvoiceLoading] = useState({});
  // ⬅️ NEU: Sichtbarkeit der Rechnungs-Tabelle pro Kunde
  const [showInvoicesByCustomerId, setShowInvoicesByCustomerId] = useState({});
  const [showOverviewByCustomerId, setShowOverviewByCustomerId] = useState({});
  // ⬇️ Monatsansicht
  const [viewMonthly, setViewMonthly] = useState(false);


  const monthKey = (d) => {
  if (!d) return 'ohne-monat';
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
};

  const toggleRow = async (row) => {
  const id = row.id;
  const willOpen = !expanded[id];
  setExpanded(prev => ({ ...prev, [id]: willOpen }));

  // default: Rechnungen zu
  if (willOpen) {
    setShowInvoicesByCustomerId(prev => ({ ...prev, [id]: prev[id] ?? false }));
  }

  if (!willOpen) return;


  const fromServer = Array.isArray(row.invoices) && row.invoices.length;
  const cachedInv = Array.isArray(invoicesByCustomerId[id]) && invoicesByCustomerId[id].length;

  try {
    setRowInvoiceLoading(prev => ({ ...prev, [id]: true }));

    // ⬇️ Wie bisher: Nur Rechnungen laden, wenn weder vom Server noch im Cache
    const needInvoices = !(fromServer || cachedInv);

    // ⬇️ NEU: Die Monatsübersicht *immer* frisch holen (wie gewünscht „wie Rechnungen reagieren“)
    const { data } = await api.get(`/reports/invoices/revenue/by-customer/${id}`, {
  params: { dateFrom, dateTo, search, includeOverview: true }
});

// Antwort robust parsen (Legacy: Array; Neu: { invoices, overview })
const parsedInvoices = Array.isArray(data)
  ? data
  : Array.isArray(data?.invoices) ? data.invoices : [];

const parsedOverview = Array.isArray(data) 
  ? [] 
  : (Array.isArray(data?.overview) ? data.overview : []);

// Rechnungen nur setzen, wenn wirklich benötigt
if (needInvoices) {
  setInvoicesByCustomerId(prev => ({ ...prev, [id]: parsedInvoices }));
}

// Übersicht IMMER frisch setzen (reagiert auf Datums-/Suchfilter)
setOverviewByCustomerId(prev => ({ ...prev, [id]: parsedOverview }));

// (Optional) Monate standardmäßig zuklappen, wenn du den Default „zu“ willst:
if (parsedOverview.length) {
  const collapsed = parsedOverview.reduce((acc, row) => {
    const dt = row.month ? new Date(row.month) : null;
    const key = dt ? `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}` : 'ohne-monat';
    if (!(key in acc)) acc[key] = false; // false = zugeklappt
    return acc;
  }, {});
  setShowOverviewByCustomerId(prev => ({
    ...prev,
    [id]: { ...(prev[id] || {}), ...collapsed }
  }));
}


    // ⬇️ Übersicht IMMER aktualisieren
    if (!Array.isArray(data)) {
      const ov = Array.isArray(data?.overview) ? data.overview : [];
      setOverviewByCustomerId(prev => ({ ...prev, [id]: ov }));

      // Optional: Monate standardmäßig zuklappen
      if (ov.length) {
        const collapsed = ov.reduce((acc, row) => {
          const dt = row.month ? new Date(row.month) : null;
          const k  = dt ? `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}` : 'ohne-monat';
          if (!(k in acc)) acc[k] = false;
          return acc;
        }, {});
        setShowOverviewByCustomerId(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...collapsed } }));
      }
    }
  } catch (e) {
    console.error(`Rechnungen/Überblick für Kunde ${id} konnten nicht geladen werden:`, e);
    // Bei Fehler: Übersicht nicht stehen lassen
    setOverviewByCustomerId(prev => ({ ...prev, [id]: [] }));
    if (!(fromServer || cachedInv)) {
      setInvoicesByCustomerId(prev => ({ ...prev, [id]: [] }));
    }
  } finally {
    setRowInvoiceLoading(prev => ({ ...prev, [id]: false }));
  }
};




  // Params für API
  const buildParams = useCallback(() => {
    const params = { page, pageSize, orderBy, orderDir };

    if (search) params.search = search;
    if (customerId) params.customerId = customerId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    if (customerCategoryId) params.customerCategoryId = customerCategoryId;
    if (customerGroupId) params.customerGroupId = customerGroupId;
    if (postalCodeFrom) params.postalCodeFrom = postalCodeFrom;
    if (postalCodeTo) params.postalCodeTo = postalCodeTo;

    // 🔹 Umsatz-Range (Netto)
    if (sumNetFrom !== '') params.sumNetFrom = sumNetFrom;
    if (sumNetTo !== '')   params.sumNetTo   = sumNetTo;

    if (viewMonthly) {
  params.view = 'monthly';
}

    return params;
  }, [
    page, pageSize, orderBy, orderDir,
    search, customerId, dateFrom, dateTo,
    customerCategoryId, customerGroupId, postalCodeFrom, postalCodeTo,
    sumNetFrom, sumNetTo, viewMonthly
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const params = buildParams();
      const { data } = await api.get('/reports/invoices/revenue', { params });
      setRows(data.data || []);
      setTotal(Number(data.total || 0));
      setPages(Math.max(1, Number(data.pages ?? 1)));
      setSumNet(Number(data.sumNet || 0));
      setSumGross(Number(data.sumGross || 0));
      setAmountPaid(Number(data.amountPaid || 0));
      setOutstanding(Number(data.outstanding || 0));
    } catch (e) {
      console.error('CustrevFromInvoiceReport laden fehlgeschlagen:', e);
      setErrMsg('Fehler beim Laden des Berichts');
      setRows([]); setTotal(0); setPages(0); setSumNet(0); setSumGross(0); setAmountPaid(0); setOutstanding(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Debounced loaders
  const loadCustomers = useMemo(() => debounce(async (q) => {
    try {
      const { data } = await api.get('/customers', { params: { search: q || '', limit: 10 } });
    const opts = (Array.isArray(data) ? data : []).map(c => ({
        value: c.id,
        label: `${c.company} (${c.customerNumber || '—'})`,
        isBlocked: !!c.isBlocked,
      }));
      setCustomerOptions(opts);
    } catch (e) {
      console.error('Kunden laden fehlgeschlagen:', e);
      setCustomerOptions([]);
    }
  }, 300), []);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get('/customerCategories', { params: { limit: 200 } });
      const opts = (Array.isArray(data) ? data : []).map(c => ({ value: c.id, label: c.name || `Kategorie ${c.id}` }));
      setCategoryOptions([{ value: '', label: 'Alle Kategorien' }, ...opts]);
    } catch {
      setCategoryOptions([{ value: '', label: 'Alle Kategorien' }]);
    }
  }, []);
  const loadGroups = useCallback(async () => {
    try {
      const { data } = await api.get('/customerGroups', { params: { limit: 200 } });
      const opts = (Array.isArray(data) ? data : []).map(g => ({ value: g.id, label: g.name || `Gruppe ${g.id}` }));
      setGroupOptions([{ value: '', label: 'Alle Gruppen' }, ...opts]);
    } catch {
      setGroupOptions([{ value: '', label: 'Alle Gruppen' }]);
    }
  }, []);

  useEffect(() => {
    loadCustomers('');
    loadCategories();
    loadGroups();
    return () => loadCustomers.cancel();
  }, [loadCustomers, loadCategories, loadGroups]);

  const handleCustomerInputChange = (inputValue, { action }) => {
    if (action === 'input-change') loadCustomers(inputValue);
  };
  const handleCustomerChange = (opt) => {
    setSelectedCustomer(opt || null);
    setCustomerId(opt ? String(opt.value) : '');
    setPage(1);
  };

  const customerOptionStyles = {
    option: (base, { data, isFocused, isSelected }) => ({
      ...base,
      color: data.isBlocked ? '#b91c1c' : base.color,
      fontWeight: data.isBlocked ? 600 : 400,
      backgroundColor: isFocused
        ? (data.isBlocked ? '#fee2e2' : base.backgroundColor)
        : isSelected ? base.backgroundColor : 'transparent',
    }),
    singleValue: (base, { data }) => ({
      ...base,
      color: data?.isBlocked ? '#b91c1c' : base.color,
      fontWeight: data?.isBlocked ? 600 : 400,
    }),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (page !== 1) setPage(1);
    else await fetchData();
  };

  // CSV
  const handleDownloadCSV = async () => {
    try {
      const params = { ...buildParams(), format: 'csv' };
      const response = await api.get('/reports/invoices/revenue', { params, responseType: 'blob' });
      const cd = response.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || 'custrev_from_invoice_report.csv';
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV-Download fehlgeschlagen:', e);
      setErrMsg('CSV-Download fehlgeschlagen');
    }
  };

  const gotoPrev = () => setPage(p => Math.max(1, p - 1));
  const gotoNext = () => setPage(p => Math.min(pages || 1, p + 1));

  // Initial + Paging
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchData(); }, [page, pageSize, fetchData]);

  // Seite bei Filterwechsel zurücksetzen
  useEffect(() => {
    setPage(1);
  }, [
    customerId, search, dateFrom, dateTo,
    customerCategoryId, customerGroupId, postalCodeFrom, postalCodeTo,
    sumNetFrom, sumNetTo, orderBy, orderDir
  ]);

  useEffect(() => {
  setPage(1);
  // bei Wechsel Monats/Kundenansicht Expand-Zustände zurücksetzen
  setExpanded({});
}, [viewMonthly]);

  const money = (v) => (Number(v || 0)).toLocaleString('de-DE', { minimumFractionDigits: 2 }) + ' €';
  const fmtDate = (d) => formatDateDE(d, '—');
  const fmtMonth = (dOrKey) => {
  if (!dOrKey) return '—';
  // unterstützt Date/ISO (z.B. monthStart) und 'YYYY-MM' (monthKey)
  const dt = typeof dOrKey === 'string' && /^\d{4}-\d{2}$/.test(dOrKey)
    ? new Date(`${dOrKey}-01T00:00:00Z`)
    : new Date(dOrKey);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit' }); // "03.2025" (bzw. "03/2025" je nach Locale)
};


// Liefert "YYYY-MM" aus einer Row (monthly mode)
const rowMonthKey = (r) => {
  if (r.monthKey) return r.monthKey;
  if (r.monthStart) {
    const dt = new Date(r.monthStart);
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
  }
  return '';
};

// Gruppiert die (seitenweise) Rows nach Monat und summiert Kennzahlen
const groupRowsByMonth = (list) => {
  const groups = new Map();
  for (const r of list || []) {
    const key = rowMonthKey(r) || 'ohne-monat';
    const g = groups.get(key) || {
      key,
      monthStart: r.monthStart ?? null,
      rows: [],
      sums: { invoiceCount: 0, sumNet: 0, sumGross: 0, amountPaid: 0, outstanding: 0 },
    };
    g.rows.push(r);
    g.sums.invoiceCount += Number(r.invoiceCount || 0);
    g.sums.sumNet       += Number(r.sumNet || 0);
    g.sums.sumGross     += Number(r.sumGross || 0);
    g.sums.amountPaid   += Number(r.amountPaid || 0);
    g.sums.outstanding  += Number(r.outstanding || 0);
    groups.set(key, g);
  }
  // Neueste Monate zuerst (DESC, passend zu deinem Backend)
  return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));
};
  const groupOverviewByMonth = (list) => {
  const groups = new Map();
  for (const r of list || []) {
    const d = r.month ? new Date(r.month) : null;
    const key = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` : 'ohne-monat';
    const g = groups.get(key) || { month: r.month, rows: [], totalRevenue: 0, totalDays: 0 };
    g.rows.push(r);
    g.totalRevenue += Number(r.revenueNet || 0);
    g.totalDays    += Number(r.rentalDays || 0);
    groups.set(key, g);
  }
  // nach Monat aufsteigend sortieren
  return Array.from(groups.values()).sort((a,b) => new Date(a.month) - new Date(b.month));
};

// Spaltenbreiten (px)
const COLW = {
  month: 84,
  paid:  120,   // schlanker
  open:  140,   // schlanker
  netMin:  140, // neu: Mindestbreite Netto
  grossMin:140, // neu: Mindestbreite Brutto
};
  return (
    <div style={{ maxWidth: 1200, margin: 'auto' }}>
      <Helmet>
        <title>Bericht: Kunden Umsatz</title>
        <meta name="Custrev From Invoice Report" content="Kundenumsatz basierend auf Rechnungen" />
      </Helmet>

      <h2>📊 Kunden Umsatz</h2>

      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          {/* Kunde */}
          <div style={{ flex: '1 1 260px', minWidth: 260 }}>
            <label htmlFor="customerSelect" style={{ display: 'inline-block', marginBottom: 4 }}>
              Kunde
            </label>
            <Select
              inputId="customerSelect"
              className="select-box-cst"
              isClearable
              placeholder="Kunde suchen…"
              options={customerOptions}
              value={selectedCustomer}
              onInputChange={handleCustomerInputChange}
              onChange={handleCustomerChange}
              getOptionValue={(o) => String(o.value)}
              styles={{
                ...customerOptionStyles,
                container: (base) => ({ ...base, marginTop: -5 }),
                control:   (base) => ({ ...base, minHeight: 34, width: '100%', marginBottom: 16 }),
              }}
            />
          </div>

          {/* Rechnungsdatum ab/bis */}
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Rechnungsdatum ab</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Rechnungsdatum bis</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: '100%' }} />
          </div>

          {/* Kategorie */}
          <div style={{ flex: '1 1 200px', minWidth: 200 }}>
            <label>Kundenkategorie</label>
            <select value={customerCategoryId} onChange={e => setCustomerCategoryId(e.target.value)} style={{ width: '100%' }}>
              {categoryOptions.map(o => <option key={o.value ?? ''} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Gruppe */}
          <div style={{ flex: '1 1 200px', minWidth: 200 }}>
            <label>Kundengruppe</label>
            <select value={customerGroupId} onChange={e => setCustomerGroupId(e.target.value)} style={{ width: '100%' }}>
              {groupOptions.map(o => <option key={o.value ?? ''} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* PLZ von/bis */}
          <div style={{ flex: '1 1 130px', minWidth: 130 }}>
            <label>PLZ von</label>
            <input
              type="text"
              value={postalCodeFrom}
              onChange={e => setPostalCodeFrom(e.target.value)}
              placeholder="z.B. 01000"
              maxLength={10}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1 1 130px', minWidth: 130 }}>
            <label>PLZ bis</label>
            <input
              type="text"
              value={postalCodeTo}
              onChange={e => setPostalCodeTo(e.target.value)}
              placeholder="z.B. 19999"
              maxLength={10}
              style={{ width: '100%' }}
            />
          </div>

          {/* 🔹 Umsatz Netto von/bis */}
          <div style={{ flex: '1 1 150px', minWidth: 150 }}>
            <label>Umsatz Netto von</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="z.B. 1.000"
              value={sumNetFrom}
              onChange={e => setSumNetFrom(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1 1 150px', minWidth: 150 }}>
            <label>Umsatz Netto bis</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="z.B. 50.000"
              value={sumNetTo}
              onChange={e => setSumNetTo(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          {/* Suche */}
          <div style={{ flex: '1 1 200px', minWidth: 200 }}>
            <label>Suche</label>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rech.-Nr., Kunde…"
              style={{ width: '100%' }}
            />
          </div>

          {/* Rechts: Sortierung + Toggle + CSV */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
  <div>
    <label style={{ display: 'block' }}>Sortierung</label>
    <div style={{ display: 'flex', gap: 8 }}>
      <select value={orderBy} onChange={e => setOrderBy(e.target.value)} style={{ width: 180 }} disabled={viewMonthly}>
        <option value="sumGross">Summe Brutto</option>
        <option value="sumNet">Summe Netto</option>
        <option value="invoiceCount">Anzahl Rechnungen</option>
        <option value="lastInvoiceDate">Letzte Rechnung</option>
      </select>
      <select value={orderDir} onChange={e => setOrderDir(e.target.value)} style={{ width: 100 }} disabled={viewMonthly}>
        <option value="DESC">DESC</option>
        <option value="ASC">ASC</option>
      </select>
    </div>
  </div>

  <button
    type="button"
    onClick={() => setViewMonthly(v => !v)}
    disabled={loading}
    title="Zwischen Kunden- und Monatsansicht wechseln"
  >
    {viewMonthly ? 'Kundenansicht' : 'Monatsansicht'}
  </button>

  <button type="button" onClick={handleDownloadCSV} disabled={loading}>CSV herunterladen</button>
</div>

        </div>
      </form>

      {errMsg && <p style={{ color: 'red' }}>{errMsg}</p>}

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <strong>Gesamt:</strong> {total} {viewMonthly ? 'Monat×Kunde-Gruppen' : 'Kunden'} &nbsp;|&nbsp;
          <strong> Netto:</strong> {money(sumNet)} &nbsp;|&nbsp;
          <strong> Brutto:</strong> {money(sumGross)} &nbsp;|&nbsp;
          <strong> Bezahlt:</strong> {money(amountPaid)} &nbsp;|&nbsp;
          <strong> Offen:</strong> {money(outstanding)}
        </div>
        <div>
          <button onClick={gotoPrev} disabled={loading || page <= 1}>Zurück</button>
          <span style={{ margin: '0 8px' }}>Seite {page} / {pages || 1}</span>
          <button onClick={gotoNext} disabled={loading || page >= (pages || 1)}>Weiter</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
         <thead>
  <tr style={{ background: '#fffefeff' }}>
    <th style={{ width: 34 }}></th>

    {viewMonthly && (
      <th
        style={{
          textAlign: 'left',
          padding: 8,
          width: COLW.month,
          minWidth: COLW.month,
          whiteSpace: 'nowrap'
        }}
      >
        Monat
      </th>
    )}

    <th style={{ textAlign: 'left', padding: 8 }}>Kunde</th>
    <th style={{ textAlign: 'left', padding: 8 }}>PLZ</th>
    <th style={{ textAlign: 'right', padding: 8 }}>RE (#)</th>
    <th style={{ textAlign: 'right', padding: 8, minWidth: COLW.netMin,  whiteSpace: 'nowrap' }}>Summe Netto (€)</th>
<th style={{ textAlign: 'right', padding: 8, minWidth: COLW.grossMin, whiteSpace: 'nowrap' }}>Summe Brutto (€)</th>

    <th
      style={{
        textAlign: 'right',
        padding: 8,
        width: COLW.paid,
        minWidth: COLW.paid,
        whiteSpace: 'nowrap'
      }}
    >
      Bezahlt (€)
    </th>
    <th
      style={{
        textAlign: 'right',
        padding: 8,
        width: COLW.open,
        minWidth: COLW.open,
        whiteSpace: 'nowrap'
      }}
    >
      Offen (€)
    </th>

    <th style={{ textAlign: 'left', padding: 8 }}>Letzte Rechnung</th>
    <th style={{ textAlign: 'left', padding: 8 }}>Letzte Zahlung</th>
  </tr>
</thead>

<tbody>
  {rows.length === 0 && !loading && (
    <tr>
      <td colSpan={viewMonthly ? 11 : 10} style={{ padding: 12, textAlign: 'center' }}>
        Keine Daten
      </td>
    </tr>
  )}

  {viewMonthly
    ? (
      /* ===== MONATSANSICHT: gruppiert + Zwischensumme pro Monat ===== */
      groupRowsByMonth(rows).map((grp) => (
        <React.Fragment key={`mgrp-${grp.key}`}>
          {grp.rows.map((r) => (
            <tr key={r.id + (r.monthKey ? '-' + r.monthKey : '')} style={{ borderTop: '1px solid #ddd' }}>
              {/* 1. Spalte: Toggle-Platzhalter (Monatsmodus = keine Rechnungsdetails) */}
              <td style={{ padding: 0, textAlign: 'center' }}>
                <span style={{ opacity: 0.35 }}>—</span>
              </td>

              {/* 2. Spalte: Monat */}
             <td
    style={{
      padding: 8,
      width: COLW.month,
      minWidth: COLW.month,
      whiteSpace: 'nowrap'
    }}
  >
    {fmtMonth(r.monthStart || r.monthKey)}
  </td>

              {/* Restliche Spalten */}
              <td style={{ padding: 8 }}>
  {r.customer?.company || '—'}
</td>
              <td style={{ padding: 8 }}>{r.customer?.postalCode || '—'}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{Number(r.invoiceCount || 0)}</td>
              <td style={{ padding: 8, textAlign: 'right', minWidth: COLW.netMin,  whiteSpace: 'nowrap' }}>
  {money(r.sumNet)}
</td>
<td style={{ padding: 8, textAlign: 'right', minWidth: COLW.grossMin, whiteSpace: 'nowrap' }}>
  {money(r.sumGross)}
</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{money(r.amountPaid)}</td>
              <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>{money(r.outstanding)}</td>
              <td style={{ padding: 8 }}>{fmtDate(r.lastInvoiceDate)}</td>
              <td style={{ padding: 8 }}>{fmtDate(r.lastPaymentDate)}</td>
            </tr>
          ))}

          {/* Zwischensumme je Monat (für die aktuell sichtbare Seite) */}
          <tr style={{ borderTop: '2px solid #bbb', background: '#f7f7f7' }}>
            <td></td>
            <td style={{ padding: 8, fontWeight: 700 }}>
              Zwischensumme {fmtMonth(grp.monthStart || grp.key)}
            </td>
            <td></td>
            <td></td>
            <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>
              {grp.sums.invoiceCount}
            </td>
            <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>
              {money(grp.sums.sumNet)}
            </td>
            <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>
              {money(grp.sums.sumGross)}
            </td>
            <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>
              {money(grp.sums.amountPaid)}
            </td>
            <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>
              {money(grp.sums.outstanding)}
            </td>
            <td></td>
            <td></td>
          </tr>
        </React.Fragment>
      ))
    )
    : (
      /* ===== KUNDENANSICHT (dein bisheriger Code unverändert) ===== */
      rows.map(r => {
        const responseInvoices = Array.isArray(r.invoices) ? r.invoices : null;
        const cachedInvoices = invoicesByCustomerId[r.id];
        const cachedOverview = overviewByCustomerId[r.id];
        const visibleInvoices = responseInvoices && responseInvoices.length ? responseInvoices : (cachedInvoices || []);
        const visibleOverview = Array.isArray(cachedOverview) ? cachedOverview : [];
        const isOpen = !!expanded[r.id];

        return (
          <React.Fragment key={r.id + (r.monthKey ? '-' + r.monthKey : '')}>
            <tr style={{ borderTop: '1px solid #ddd' }}>
             <td style={{ padding: 0, textAlign: 'center' }}>
  <button
    type="button"
    onClick={() => toggleRow(r)}
    title={isOpen ? 'Rechnungen verbergen' : 'Rechnungen anzeigen'}
    aria-label={isOpen ? 'Rechnungen verbergen' : 'Rechnungen anzeigen'}
    style={{
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      padding: 4,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 16,
    }}
  >
    <span style={{ fontSize: 16 }}>📂</span>
  </button>
</td>


              <td style={{ padding: 8 }}>{r.customer?.company || '—'}</td>
              <td style={{ padding: 8 }}>{r.customer?.postalCode || '—'}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{Number(r.invoiceCount || 0)}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{money(r.sumNet)}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{money(r.sumGross)}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{money(r.amountPaid)}</td>
              <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>{money(r.outstanding)}</td>
              <td style={{ padding: 8 }}>{fmtDate(r.lastInvoiceDate)}</td>
              <td style={{ padding: 8 }}>{fmtDate(r.lastPaymentDate)}</td>
            </tr>

            {!viewMonthly && isOpen && (
              <tr>
                <td colSpan={10} style={{ background: '#fff', padding: 8 }}>
                  {/* dein bestehender Expand-Inhalt (Rechnungen + Überblick) */}
                  {rowInvoiceLoading[r.id] ? (
                    <div style={{ padding: 6 }}>Rechnungen werden geladen…</div>
                  ) : (
                    <>
                      {/* ===== START: Rechnungen Toggle (+/−), default: zu ===== */}
                      {(() => {
                        const invShown = !!(showInvoicesByCustomerId[r.id] ?? false);
                        return (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                              <button
                                type="button"
                                onClick={() =>
                                  setShowInvoicesByCustomerId(prev => ({ ...prev, [r.id]: !invShown }))
                                }
                                aria-expanded={invShown}
                                aria-controls={`inv-table-${r.id}`}
                                title={invShown ? 'Rechnungen verbergen' : 'Rechnungen anzeigen'}
                                style={{
                                  width: 24,
                                  height: 24,
                                  border: '1px solid #999',
                                  borderRadius: 4,
                                  background: '#fff',
                                  color: '#111',
                                  cursor: 'pointer',
                                  lineHeight: '22px',
                                  textAlign: 'center',
                                  padding: 0,
                                  fontSize: 16,
                                  fontWeight: 700,
                                  userSelect: 'none'
                                }}
                              >
                                {invShown ? '-' : '+'}
                              </button>

                              <span style={{ fontSize: 12, opacity: 0.8 }}>Rechnungen</span>
                            </div>

                            {invShown && (
                              visibleInvoices.length === 0 ? (
                                <div style={{ padding: 6 }}>Keine Rechnungen vorhanden.</div>
                              ) : (
                                <table
                                  id={`inv-table-${r.id}`}
                                  style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}
                                >
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: 'left',  padding: 6 }}>Rech.-Nr.</th>
                                      <th style={{ textAlign: 'left',  padding: 6 }}>Datum</th>
                                      <th style={{ textAlign: 'right', padding: 6 }}>Netto</th>
                                      <th style={{ textAlign: 'right', padding: 6 }}>Brutto</th>
                                      <th style={{ textAlign: 'right', padding: 6 }}>Bezahlt</th>
                                      <th style={{ textAlign: 'right', padding: 6 }}>Offen</th>
                                      <th style={{ textAlign: 'left',  padding: 6 }}>Letzte Zahlung</th>
                                      <th style={{ textAlign: 'left',  padding: 6 }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {visibleInvoices.map(inv => (
                                      <tr key={inv.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: 6 }}>{inv.invoiceNumber || '—'}</td>
                                        <td style={{ padding: 6 }}>{fmtDate(inv.date)}</td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>{money(inv.totalNet)}</td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>{money(inv.totalGross)}</td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>{money(inv.amountPaid)}</td>
                                        <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>{money(inv.openAmount)}</td>
                                        <td style={{ padding: 6 }}>{fmtDate(inv.lastPaymentDate)}</td>
                                        <td style={{ padding: 6 }}>
                                          {inv.paymentState === 'paid' ? 'Bezahlt'
                                            : inv.paymentState === 'partial' ? 'Teilzahlung' : 'Unbezahlt'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )
                            )}
                          </>
                        );
                      })()}
                      {/* ===== END: Rechnungen Toggle ===== */}

                      {/* ===== Überblick Monat / Artikel / Umsatz (Netto) / Miettage ===== */}
                      <div style={{ marginTop: 8, marginBottom: 4, fontWeight: 600, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <span>Überblick: Monat / Artikel / Umsatz (Netto) / Miettage</span>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const params = { dateFrom, dateTo, search, includeOverview: true, format: 'csv' };
                              const response = await api.get(`/reports/invoices/revenue/by-customer/${r.id}`, { params, responseType: 'blob' });
                              const cd = response.headers['content-disposition'] || '';
                              const m = cd.match(/filename="?([^"]+)"?/i);
                              const filename = m?.[1] || `custrev_overview_customer_${r.id}.csv`;
                              const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = filename;
                              document.body.appendChild(a); a.click(); a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (e) {
                              console.error('CSV-Übersicht Download fehlgeschlagen:', e);
                              setErrMsg('CSV-Übersicht Download fehlgeschlagen');
                            }
                          }}
                          style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
                          title="CSV-Export der Monatsübersicht"
                        >
                          CSV
                        </button>
                      </div>

                      {visibleOverview.length === 0 ? (
                        <div style={{ padding: 6, opacity: 0.8 }}>Keine Übersicht verfügbar.</div>
                      ) : (
                        groupOverviewByMonth(visibleOverview).map((grp, gi) => {
                          const key = monthKey(grp.month);
                          const isShown = showOverviewByCustomerId[r.id]?.[key] ?? false;
                          return (
                            <div key={`grp-${gi}`} style={{ marginBottom: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowOverviewByCustomerId(prev => ({
                                      ...prev,
                                      [r.id]: { ...(prev[r.id] || {}), [key]: !(prev[r.id]?.[key] ?? false) }
                                    }))
                                  }
                                  aria-expanded={isShown}
                                  title={isShown ? 'Artikelpositionen verbergen' : 'Artikelpositionen anzeigen'}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    border: '1px solid #999',
                                    borderRadius: 4,
                                    background: '#fff',
                                    color: '#111',
                                    cursor: 'pointer',
                                    lineHeight: '22px',
                                    textAlign: 'center',
                                    padding: 0,
                                    fontSize: 16,
                                    fontWeight: 700,
                                    userSelect: 'none'
                                  }}
                                >
                                  {isShown ? '-' : '+'}
                                </button>
                                <div style={{ fontWeight: 600 }}>
                                  Monat: {fmtMonth(grp.month)}
                                </div>
                              </div>

                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left',  padding: 6 }}>Artikel</th>
                                    <th style={{ textAlign: 'right', padding: 6 }}>Umsatz (Netto)</th>
                                    <th style={{ textAlign: 'right', padding: 6 }}>Miettage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {isShown && grp.rows.map((ov, idx) => (
                                    <tr key={`${ov.itemId}-${idx}`} style={{ borderTop: '1px solid #f0f0f0' }}>
                                      <td style={{ padding: 6 }}>{ov.itemName || `#${ov.itemId}`}</td>
                                      <td style={{ padding: 6, textAlign: 'right' }}>{money(ov.revenueNet)}</td>
                                      <td style={{ padding: 6, textAlign: 'right' }}>{Number(ov.rentalDays || 0)}</td>
                                    </tr>
                                  ))}
                                  {/* Zwischensumme immer sichtbar */}
                                  <tr style={{ borderTop: '2px solid #ddd', background: '#fafafa' }}>
                                    <td style={{ padding: 6, fontWeight: 700 }}>Zwischensumme {fmtMonth(grp.month)}</td>
                                    <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>{money(grp.totalRevenue)}</td>
                                    <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>{Number(grp.totalDays)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          );
                        })
                      )}
                      {/* ===== Ende Überblick ===== */}
                    </>
                  )}
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })
    )
  }
</tbody>

        </table>
      </div>

      {loading && <p style={{ marginTop: 8 }}>Lade…</p>}
    </div>
  );
}

export default CustrevFromInvoiceReport;
